import * as React from "react";
import {useCallback} from "react";
import { atom, Getter, useSetAtom } from 'jotai';
import { atomFamily } from 'jotai/utils';

/**
 * Column definition for RangeList
 */
export interface RangeListColumn<T> {
   id: string
   label: string
   field?: string
   width?: number
   /** Minimum row height required by this column (default: 45) */
   minHeight?: number
   align?: 'left' | 'center' | 'right' | 'justify'
   get?: (row: T) => string | null | undefined | any | any[] | number
   render?: (data: any, row?: T, context?: any) => React.ReactNode
   ellipsis?: boolean
}

/**
 * Selection state for RangeList
 */
export interface RangeSelection {
   /** Array of selected row IDs */
   selected: string[];
}

/**
 * Creates an atom family with cleanup on unmount
 */
function createCleanableAtomFamily<T extends object>(defaultValue: T) {
   return atomFamily((globalId: string) => {
      const baseAtom = atom<T>(defaultValue);

      baseAtom.onMount = (setAtom) => {
         setAtom((prev) => prev);
         return () => {
            setAtom(defaultValue);
         };
      };

      return baseAtom;
   });
}

/**
 * Jotai atom for storing selection state per RangeList instance
 */
export const rangeSelectionAtom = createCleanableAtomFamily<RangeSelection>({
   selected: []
});

/**
 * Pending row reloads for RangeList - stores IDs of rows to reload
 */
export interface RangePendingReloads {
   ids: string[];
}

/**
 * Jotai atom for pending row reloads per RangeList instance.
 * Components outside the list can write IDs here, and RangeList will reload them.
 * Note: Uses regular atomFamily (not cleanable) so pending IDs persist across navigation.
 */
export const rangePendingReloadsAtom = atomFamily((globalId: string) =>
   atom<RangePendingReloads>({ids: []})
);

/**
 * Hook for reloading rows in a RangeList from external components (e.g., detail pages).
 * @param globalId - The globalId of the RangeList to reload rows in
 * @returns reloadRows function that accepts an array of row IDs to reload
 */
export const useRangeListUpdater = (globalId: string) => {
   const setPendingReloads = useSetAtom(rangePendingReloadsAtom(globalId));

   const reloadRows = useCallback((ids: string[]) => {
      setPendingReloads(prev => ({
         ids: [...prev.ids, ...ids]
      }));
   }, [setPendingReloads]);

   return { reloadRows };
};

/**
 * Column settings for RangeList - manages visible columns
 */
export interface RangeColumnsSettings {
   /** All available columns */
   columns: RangeListColumn<any>[];
   /** Currently visible column IDs (undefined means show all) */
   columnsVisible?: string[];
   /** Tab's default column IDs (for reset functionality) */
   defaultColumnsVisible?: string[];
}

/**
 * Jotai atom for storing column settings per RangeList instance
 */
export const rangeColumnsSettingsAtom = createCleanableAtomFamily<RangeColumnsSettings>({
   columns: [],
   columnsVisible: undefined,
   defaultColumnsVisible: undefined
});

/**
 * Derived atom that computes visible columns based on columnsVisible setting
 */
export const rangeListColumnsAtom = atomFamily((globalId: string) => {
   const baseAtom = rangeColumnsSettingsAtom(globalId);

   return atom((get: Getter) => {
      const settings = get(baseAtom);
      return settings?.columns?.filter(column => {
         if (settings.columnsVisible) {
            return settings.columnsVisible.includes(column.id);
         }
         return true;
      }) || [];
   });
});

/**
 * Command definition for RangeList selection actions
 */
export interface RangeCommand<T> {
   /** Unique identifier for the command */
   id: string;
   /** Display title for the command button */
   title: string;
   /** Icon component to display */
   icon: React.ComponentType<any>;
   /**
    * Execute function called with selected row IDs.
    * Optionally returns partial row updates to merge into cached data.
    * Each update object must include the id field (e.g., _id) to identify the row.
    */
   execute?: (rowIds: string[]) => Promise<Partial<T>[] | void>;
   /** Child commands for dropdown menu */
   children?: RangeCommand<T>[];
}

/**
 * Filter value tracking - tracks which options are selected for a filter
 */
export type RangeFilterValue = {
   filterId: string
   values: string[]
   requireAll?: boolean
}

/**
 * Filter definition with available options
 */
export type RangeFilter<F> = {
   id: string
   label: string
   options: RangeFilterOption<F>[]
}

/**
 * Individual filter option
 */
export type RangeFilterOption<F> = {
   id?: string
   label: string
   subtext?: string
   filter: F
}

/**
 * Sort definition (for future use)
 */
export type RangeSort<F> = {
   id: string
   label: string
   value: F
}

/**
 * Tab definition for RangeList
 */
export type RangeTab = {
   /** Unique identifier for the tab */
   id: string
   /** Display label for the tab */
   label: string
   /** Predefined filters applied when tab is selected */
   filters: RangeFilterValue[]
   /** Optional column visibility for this tab (include list) */
   columns?: string[]
   /** Optional columns to exclude for this tab (exclude list, applied after columns) */
   columnsExcluded?: string[]
   /**
    * Columns this tab always shows, even when the user has saved their own column selection.
    * The tab's `columns` list still decides where they appear.
    */
   columnsForced?: string[]
   /** Whether to show a count badge on this tab */
   showCount?: boolean
   /** The count to display (loaded automatically if showCount is true) */
   count?: number
   /** Optional separate filters for count calculation (uses filters if not specified) */
   countFilters?: RangeFilterValue[]
}

/**
 * Response type for onRequestData callback
 */
export interface RangeListResponse<T> {
   /** The items for the requested page */
   data: T[];
   /** Total count of items in the dataset */
   totalCount: number;
}

/**
 * Props for the RangeList component
 */
export interface RangeListProps<T> {
   /** Unique identifier for this list instance */
   globalId: string;

   /** Column definitions */
   columns: RangeListColumn<T>[];

   /** Number of items to fetch per page/request */
   itemsPerPage: number;

   /**
    * Callback to fetch data for a specific page
    * @param search - Search string (undefined if no search)
    * @param sort - Sort value (undefined if no sort)
    * @param page - 1-indexed page number
    * @param perPage - Number of items per page
    * @param filter - Optional GraphQL filter object built from selected filters
    * @returns Promise resolving to data and totalCount
    */
   onRequestData: (search: string | undefined, sort: string | undefined, page: number, perPage: number, filter?: any) => Promise<RangeListResponse<T>>;

   /** Optional configuration */
   options?: Partial<RangeListOptions>;

   /** Callback when a row is clicked */
   onSelectRow?: (row: T, newWindow?: boolean) => void;

   /** Additional context passed to column render functions */
   context?: any;

   /** Range filters */
   filters?: RangeFilter<any>[];

   /** Sort options (for future use) */
   sorts?: RangeSort<any>[];

   /** Search input label */
   searchLabel?: string;

   /** Tab definitions */
   tabs?: RangeTab[];

   /** Enable row selection mode with checkboxes */
   selections?: boolean;

   /**
    * Callback to fetch all IDs matching current filter (for select-all).
    * If not provided, the header checkbox is not shown (only individual row selection).
    * @param filter - The current GraphQL filter object
    * @returns Promise resolving to array of all matching row IDs
    */
   onSelectAll?: (filter?: any) => Promise<string[]>;

   /** Commands to show when rows are selected */
   commands?: RangeCommand<T>[];

   /** Initial search value (used when disableUrlState is true) */
   initialSearch?: string;

   /** Initial filter values (used when disableUrlState is true) */
   initialFilters?: RangeFilterValue[];

   /** Callback when the active tab changes */
   onTabChange?: (tabId: string) => void;

   /** Callback when the search value changes */
   onSearchChange?: (search: string) => void;

   /**
    * Callback to reload specific rows by ID.
    * Used by useRangeListUpdater hook to refresh rows after external updates.
    * @param ids - Array of row IDs to reload
    * @returns Promise resolving to the reloaded row data
    */
   onReloadRows?: (ids: string[]) => Promise<T[]>;
}

/**
 * Configuration options for RangeList
 */
export interface RangeListOptions {
   /** Field name to use as unique row identifier (default: '_id') */
   idField: string;

   /** Fixed height for each row in pixels (default: 45) */
   itemHeight: number;

   /** Fixed height for the list container - number (px) or string (e.g. 'calc(100vh - 200px)'). If not set, uses window scroll */
   height: number | string;

   /** Disable URL state management (useful for dialogs/modals) */
   disableUrlState: boolean;
}

/**
 * Internal state for tracking loaded data
 */
export interface RangeListData<T> {
   /** Sparse map of loaded items by index */
   items: Map<number, T>;

   /** Set of page numbers currently being loaded */
   loadingPages: Set<number>;

   /** Total count of items - null until first fetch completes */
   totalCount: number | null;
}
